// After signing in: the person's workspaces (and invitations), a way to make one, and their profile.
import { defaultWorkspace, setDefaultWorkspace } from "../preferences.js";
import { api } from "../api.js";
import { h } from "../dom.js";
import { setLang, t, tn } from "../i18n.js";
import { go, workspacePath, registerWorkspaces } from "../router.js";
import { act, avatar, errorLine, nameOf } from "../ui.js";

/** @param {{ user: any, onSignedOut: () => void, onChanged: () => void }} opts */
export async function settingsPage({ user, onSignedOut, onChanged }) {
  const { workspaces } = await api("GET", "/api/ws");
  registerWorkspaces(workspaces);
  return h("div", { class: "page settings-page stack" },
    topbar(user, onSignedOut),
    h("div", { class: "settings-grid" }, workspacesCard(workspaces, onChanged), h("div", { class: "stack" }, defaultPicker(user, workspaces), profileCard(user, onChanged))),
  );
}

export function settingsLink() {
  const link = h("a", { class: "btn quiet settings-link", href: "/settings", "data-link": true, "aria-label": t("appSettings"), title: t("appSettings") });
  link.innerHTML = '<svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" stroke-width="1.6" aria-hidden="true"><path d="m9 3-.6 2.5-2 .9L4 5.7 2 9l1.9 1.8v2.4L2 15l2 3.3 2.4-.7 2 .9L9 21h6l.6-2.5 2-.9 2.4.7 2-3.3-1.9-1.8v-2.4L22 9l-2-3.3-2.4.7-2-.9L15 3Z"/><circle cx="12" cy="12" r="3"/></svg>';
  return link;
}
export function homePage({ workspaces, onChanged }) {
  const start = h("div", { class: "welcome-form", hidden: true }, createForm());
  const cta = h("button", { class: "btn primary", onclick: () => { start.hidden = false; cta.hidden = true; /** @type {HTMLInputElement} */ (start.querySelector("input[name=name]")).focus(); } }, '+ ' + t("newWorkspace"));
  return h("div", { class: "welcome-page" }, h("header", { class: "welcome-header" }, settingsLink()),
    h("section", { class: "welcome-content" }, h("span", { class: "welcome-mark", "aria-hidden": "true" }, '✦'), h("h1", {}, t("welcome")), h("p", { class: "muted" }, t("welcomeHint")), cta, start,
      workspaces.some(w => w.status === "pending") && workspacesCard(workspaces, onChanged)));
}
function defaultPicker(user, workspaces) {
  const select = h("select", { "aria-label": t("defaultWorkspace"), onchange: () => setDefaultWorkspace(user.id, select.value) },
    h("option", { value: "" }, t("lastWorkspace")), ...workspaces.filter(w => w.status === "active").map(w => h("option", { value: w.id }, w.name)));
  select.value = defaultWorkspace(user.id);
  return h("section", { class: "card stack" }, h("label", {}, t("defaultWorkspace"), select));
}

export function topbar(user, onSignedOut) {
  return h("header", { class: "topbar" },
    h("div", { class: "row" }, avatar(user), h("strong", {}, nameOf(user))),
    h("a", { class: "btn quiet", href: "/", "data-link": true }, t("enterWorkspace")),
    h("button", { class: "btn quiet", onclick: async () => { await api("POST", "/api/auth/logout"); onSignedOut(); } }, t("signOut")),
  );
}

function workspacesCard(list, onChanged) {
  const pending = list.filter((w) => w.status === "pending"), active = list.filter((w) => w.status === "active");
  const err = errorLine();
  const invite = (w) => {
    const yes = h("button", { class: "btn primary" }, t("accept")), no = h("button", { class: "btn" }, t("decline"));
    yes.onclick = () => act(yes, err, async () => { await api("POST", `/api/ws/${w.id}/invite/accept`); go(workspacePath(w.id)); });
    no.onclick = () => act(no, err, async () => { await api("POST", `/api/ws/${w.id}/invite/decline`); onChanged(); });
    return h("li", { class: "item invite" },
      h("div", { class: "grow" }, h("strong", {}, w.name), h("div", { class: "muted small" }, t("invitedYou"))),
      yes, no);
  };
  const item = (w) => h("li", {},
    h("a", { class: "item link", href: workspacePath(w.id), "data-link": true },
      h("div", { class: "grow" }, h("strong", {}, w.name), h("div", { class: "muted small" }, tn("members", w.members))),
      h("span", { class: "badge" }, t(w.level))));

  return h("section", { class: "card stack" },
    h("h2", {}, t("myWorkspaces")),
    pending.length > 0 && h("ul", { class: "list" }, ...pending.map(invite)),
    active.length ? h("ul", { class: "list" }, ...active.map(item)) : h("p", { class: "muted empty" }, t("noWorkspaces")),
    err,
    createForm(),
  );
}

export function createForm() {
  const name = h("input", { name: "name", maxlength: 80, required: true, placeholder: t("workspaceName") });
  const slug = h("input", { name: "slug", dir: "ltr", placeholder: "team-id", "aria-label": t("teamId"), pattern: "[a-z0-9][a-z0-9-]{1,30}[a-z0-9]", maxlength: 32 });
  const err = errorLine(), submit = h("button", { class: "btn primary", type: "submit" }, t("create"));
  return h("form", {
    class: "stack",
    onsubmit: (e) => {
      e.preventDefault();
      act(submit, err, async () => {
        const { id } = await api("POST", "/api/ws", { name: name.value, slug: slug.value || undefined });
        go(workspacePath(id));
      });
    },
  },
    h("h3", {}, t("newWorkspace")),
    h("div", { class: "row nowrap" }, name, submit),
    h("label", {}, t("teamId"), slug),
    err,
  );
}

function profileCard(user, onChanged) {
  const field = (name, label, value, attrs = {}) => h("label", {}, label, h("input", { name, value, ...attrs }));
  const pick = (name, label, value, options) =>
    h("label", {}, label, h("select", { name }, ...options.map(([v, text]) => h("option", { value: String(v), selected: String(v) === String(value) }, text))));
  const err = errorLine(), submit = h("button", { class: "btn primary", type: "submit" }, t("save"));

  return h("form", {
    class: "card stack",
    onsubmit: (e) => {
      e.preventDefault();
      const f = new FormData(/** @type {HTMLFormElement} */ (e.currentTarget));
      act(submit, err, async () => {
        const { user: next } = await api("PATCH", "/api/me", {
          display: f.get("display"), about: f.get("about"), email: f.get("email"),
          lang: f.get("lang"), cal: f.get("cal"), weekStart: Number(f.get("weekStart")),
        });
        if (next.lang !== user.lang) setLang(next.lang);
        onChanged();
      });
    },
  },
    h("h2", {}, t("profile")),
    h("p", { class: "muted", dir: "ltr" }, "@" + user.username),
    field("display", t("display"), user.display, { maxlength: 40 }),
    field("about", t("about"), user.about, { maxlength: 160 }),
    field("email", t("email"), user.email, { type: "email", dir: "ltr" }),
    pick("lang", t("lang"), user.lang, [["fa", "فارسی"], ["en", "English"]]),
    pick("cal", t("cal"), user.cal, [["shamsi", t("shamsi")], ["gregorian", t("gregorian")]]),
    pick("weekStart", t("weekStart"), user.weekStart, [[6, t("sat")], [0, t("sun")], [1, t("mon")]]),
    err,
    submit,
  );
}
